import { motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import type { Shipment } from '../../../lib/supabase';
import ShipmentReceipt from './ShipmentReceipt';
import WorldMapTracker from '../../../components/feature/WorldMapTracker';
import { useState } from 'react';
import QRCode from 'qrcode';

interface TrackingResultsProps {
  shipment: Shipment | null;
  loading: boolean;
  error: string | null;
}

const TrackingResults = ({ shipment, loading, error }: TrackingResultsProps) => {
  const [showReceipt, setShowReceipt] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [showQR, setShowQR] = useState(false);
  const qrCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (shipment?.tracking_number) {
      // Utilise le vrai domaine publié, pas le domaine de prévisualisation Readdy
      const siteUrl = import.meta.env.VITE_SITE_URL || window.location.origin;
      const trackingUrl = `${siteUrl}/tracking?track=${shipment.tracking_number}`;
      QRCode.toDataURL(trackingUrl, {
        width: 200,
        margin: 2,
        color: { dark: '#1a1a2e', light: '#ffffff' },
      }).then(setQrDataUrl).catch(console.error);
    }
  }, [shipment?.tracking_number]);

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl p-8 border border-gray-100"
      >
        <div className="flex flex-col items-center justify-center py-16 gap-4">
          <div className="w-14 h-14 border-4 border-[#0066CC] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-500 font-medium">Recherche en cours...</p>
        </div>
      </motion.div>
    );
  }

  if (error || !shipment) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl p-8 border border-gray-100"
      >
        <div className="flex flex-col items-center justify-center py-16 gap-4">
          <div className="w-16 h-16 flex items-center justify-center bg-red-50 rounded-full">
            <i className="ri-search-eye-line text-3xl text-red-500"></i>
          </div>
          <h3 className="text-xl font-bold text-gray-900">Colis introuvable</h3>
          <p className="text-gray-500 text-center max-w-sm">
            {error || 'Aucun colis trouvé avec ce numéro de suivi. Vérifiez le numéro et réessayez.'}
          </p>
        </div>
      </motion.div>
    );
  }

  const getStatusConfig = (status: string) => {
    const s = status.toLowerCase();
    if (s === 'delivered') return { bg: 'bg-emerald-500', light: 'bg-emerald-50', text: 'text-emerald-700', icon: 'ri-checkbox-circle-fill', label: 'Livré' };
    if (s === 'in transit' || s === 'in-transit') return { bg: 'bg-[#0066CC]', light: 'bg-blue-50', text: 'text-[#0066CC]', icon: 'ri-ship-line', label: 'En transit' };
    if (s === 'out for delivery' || s === 'out-for-delivery') return { bg: 'bg-[#00CC88]', light: 'bg-emerald-50', text: 'text-[#00CC88]', icon: 'ri-truck-line', label: 'En livraison' };
    if (s === 'delayed') return { bg: 'bg-orange-500', light: 'bg-orange-50', text: 'text-orange-700', icon: 'ri-time-line', label: 'Retardé' };
    if (s === 'returned') return { bg: 'bg-orange-600', light: 'bg-orange-50', text: 'text-orange-700', icon: 'ri-arrow-go-back-line', label: 'Retourné' };
    if (s === 'cancelled') return { bg: 'bg-red-500', light: 'bg-red-50', text: 'text-red-700', icon: 'ri-close-circle-line', label: 'Annulé' };
    if (s === 'customs') return { bg: 'bg-amber-500', light: 'bg-amber-50', text: 'text-amber-700', icon: 'ri-file-list-3-line', label: 'En douane' };
    if (s === 'picked up' || s === 'picked-up') return { bg: 'bg-teal-500', light: 'bg-teal-50', text: 'text-teal-700', icon: 'ri-hand-coin-line', label: 'Collecté' };
    return { bg: 'bg-gray-500', light: 'bg-gray-50', text: 'text-gray-700', icon: 'ri-box-3-line', label: status };
  };

  const statusConfig = getStatusConfig(shipment.status);

  const formatDate = (dateString: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  };

  const hasStopovers = shipment.stopovers && Array.isArray(shipment.stopovers) && shipment.stopovers.length > 0;

  const getProgressStep = () => {
    const s = shipment.status.toLowerCase();
    if (s === 'pending') return 0;
    if (s === 'picked up' || s === 'picked-up') return 1;
    if (s === 'in transit' || s === 'in-transit' || s === 'customs') return 2;
    if (s === 'out for delivery' || s === 'out-for-delivery') return 3;
    if (s === 'delivered') return 4;
    return 1;
  };

  const progressStep = getProgressStep();
  const progressSteps = [
    { icon: 'ri-shopping-cart-line', label: 'Commandé' },
    { icon: 'ri-hand-coin-line', label: 'Collecté' },
    { icon: 'ri-ship-line', label: 'En transit' },
    { icon: 'ri-truck-line', label: 'En livraison' },
    { icon: 'ri-checkbox-circle-line', label: 'Livré' },
  ];

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-4"
      >
        {/* ── Hero Card ── */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {/* Status banner */}
          <div className={`${statusConfig.bg} px-4 sm:px-8 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl">
                <i className={`${statusConfig.icon} text-white text-xl`}></i>
              </div>
              <div>
                <div className="text-white/80 text-xs font-medium uppercase tracking-wider">Statut actuel</div>
                <div className="text-white text-lg font-bold">{statusConfig.label}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setShowQR(!showQR)}
                className="inline-flex items-center gap-2 px-3 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-qr-code-line"></i>
                QR Code
              </button>
              <button
                onClick={() => setShowReceipt(true)}
                className="inline-flex items-center gap-2 px-3 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-download-2-line"></i>
                Reçu
              </button>
            </div>
          </div>

          {/* Tracking number + QR */}
          <div className="px-4 sm:px-8 py-5 border-b border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-1">Numéro de suivi</div>
                <div className="text-xl sm:text-2xl font-bold text-gray-900 font-mono break-all">{shipment.tracking_number}</div>
                {shipment.carrier && (
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <i className="ri-truck-line text-gray-400 text-sm"></i>
                    <span className="text-sm text-gray-500">{shipment.carrier}</span>
                  </div>
                )}
              </div>
              {qrDataUrl && showQR && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200"
                >
                  <img src={qrDataUrl} alt="QR Code de suivi" className="w-24 h-24" />
                  <span className="text-xs text-gray-500 text-center">Scanner pour suivre</span>
                </motion.div>
              )}
            </div>
          </div>

          {/* Progress bar */}
          {shipment.status.toLowerCase() !== 'cancelled' && shipment.status.toLowerCase() !== 'returned' && (
            <div className="px-4 sm:px-8 py-6">
              <div className="relative">
                {/* Track line */}
                <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200 mx-8 sm:mx-12"></div>
                <div
                  className={`absolute top-5 left-0 h-0.5 ${statusConfig.bg} mx-8 sm:mx-12 transition-all duration-700`}
                  style={{ width: `${(progressStep / (progressSteps.length - 1)) * 100}%` }}
                ></div>
                <div className="relative flex justify-between">
                  {progressSteps.map((step, i) => (
                    <div key={i} className="flex flex-col items-center gap-2">
                      <div className={`w-10 h-10 flex items-center justify-center rounded-full border-2 transition-all ${
                        i <= progressStep
                          ? `${statusConfig.bg} border-transparent`
                          : 'bg-white border-gray-200'
                      }`}>
                        <i className={`${step.icon} text-sm ${i <= progressStep ? 'text-white' : 'text-gray-400'}`}></i>
                      </div>
                      <span className={`text-xs font-medium hidden sm:block ${i <= progressStep ? statusConfig.text : 'text-gray-400'}`}>
                        {step.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Alerte arrêt ── */}
        {shipment.is_stopped && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-5 flex items-start gap-4">
            <div className="w-10 h-10 flex items-center justify-center bg-red-500 rounded-xl flex-shrink-0">
              <i className="ri-alert-line text-white text-lg"></i>
            </div>
            <div>
              <h3 className="font-bold text-red-900 mb-1">Envoi arrêté</h3>
              <p className="text-red-700 text-sm">{shipment.stop_reason || 'Cet envoi a été temporairement arrêté.'}</p>
            </div>
          </div>
        )}

        {/* ── Carte du monde ── */}
        {(shipment.sender_country || shipment.origin) && (shipment.recipient_country || shipment.destination) && (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-100 flex items-center gap-2">
              <i className="ri-earth-line text-[#0066CC] text-lg"></i>
              <h3 className="font-semibold text-gray-900">Carte du trajet</h3>
            </div>
            <div className="p-4 sm:p-6">
              <WorldMapTracker
                origin={shipment.sender_country || shipment.origin}
                destination={shipment.recipient_country || shipment.destination}
                stopovers={shipment.stopovers}
                transportMode={shipment.transport_mode}
                currentLocation={shipment.current_location}
                isStopped={shipment.is_stopped}
                stopReason={shipment.stop_reason}
                carrier={shipment.carrier}
              />
            </div>
          </div>
        )}

        {/* ── Itinéraire ── */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-100 flex items-center gap-2">
            <i className="ri-route-line text-[#0066CC] text-lg"></i>
            <h3 className="font-semibold text-gray-900">Itinéraire</h3>
          </div>
          <div className="p-4 sm:p-6">
            <div className="flex items-start gap-0 overflow-x-auto pb-2 -mx-1 px-1">
              {/* Origine */}
              <div className="flex flex-col items-center min-w-[100px] sm:min-w-[130px] flex-shrink-0">
                <div className="w-11 h-11 flex items-center justify-center bg-[#0066CC] rounded-full mb-2">
                  <i className="ri-map-pin-line text-white text-lg"></i>
                </div>
                <div className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">Origine</div>
                <div className="text-xs sm:text-sm font-semibold text-gray-900 text-center px-1">{shipment.sender_country || shipment.origin}</div>
              </div>
              <div className="flex-1 min-w-[30px] flex items-center mt-4">
                <div className="w-full h-0.5 bg-gray-200 relative">
                  <i className="ri-arrow-right-s-line absolute -right-2 -top-2.5 text-gray-400"></i>
                </div>
              </div>
              {hasStopovers && shipment.stopovers!.map((stopover, index) => (
                <div key={index} className="contents">
                  <div className="flex flex-col items-center min-w-[100px] sm:min-w-[130px] flex-shrink-0">
                    <div className="w-9 h-9 flex items-center justify-center bg-orange-400 rounded-full mb-2">
                      <i className="ri-map-pin-2-line text-white text-base"></i>
                    </div>
                    <div className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">Escale {index + 1}</div>
                    <div className="text-xs sm:text-sm font-semibold text-gray-900 text-center px-1">{stopover}</div>
                  </div>
                  <div className="flex-1 min-w-[30px] flex items-center mt-4">
                    <div className="w-full h-0.5 bg-gray-200 relative">
                      <i className="ri-arrow-right-s-line absolute -right-2 -top-2.5 text-gray-400"></i>
                    </div>
                  </div>
                </div>
              ))}
              {/* Destination */}
              <div className="flex flex-col items-center min-w-[100px] sm:min-w-[130px] flex-shrink-0">
                <div className="w-11 h-11 flex items-center justify-center bg-[#00CC88] rounded-full mb-2">
                  <i className="ri-flag-line text-white text-lg"></i>
                </div>
                <div className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">Destination</div>
                <div className="text-xs sm:text-sm font-semibold text-gray-900 text-center px-1">{shipment.recipient_country || shipment.destination}</div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Infos principales en grille ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 flex items-center justify-center bg-gray-100 rounded-xl">
                <i className={`${shipment.transport_mode === 'sea' ? 'ri-ship-2-line' : 'ri-plane-line'} text-gray-600`}></i>
              </div>
              <span className="text-sm font-medium text-gray-500">Service</span>
            </div>
            <div className="font-bold text-gray-900">{shipment.service}</div>
            {shipment.transport_mode && (
              <div className="text-xs text-gray-400 mt-1">
                {shipment.transport_mode === 'sea' ? 'Transport maritime' : 'Transport aérien'}
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 flex items-center justify-center bg-blue-50 rounded-xl">
                <i className="ri-scales-3-line text-[#0066CC]"></i>
              </div>
              <span className="text-sm font-medium text-gray-500">Poids</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{shipment.weight} <span className="text-base font-medium text-gray-400">kg</span></div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 flex items-center justify-center bg-emerald-50 rounded-xl">
                <i className="ri-money-dollar-circle-line text-emerald-600"></i>
              </div>
              <span className="text-sm font-medium text-gray-500">Valeur déclarée</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">${shipment.value.toLocaleString()}</div>
          </div>
        </div>

        {/* ── Dates ── */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-100 flex items-center gap-2">
            <i className="ri-calendar-line text-[#0066CC] text-lg"></i>
            <h3 className="font-semibold text-gray-900">Dates importantes</h3>
          </div>
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {shipment.order_date && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="w-9 h-9 flex items-center justify-center bg-gray-200 rounded-lg flex-shrink-0">
                    <i className="ri-shopping-cart-line text-gray-600 text-sm"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Commande</div>
                    <div className="text-sm font-semibold text-gray-900">{formatDate(shipment.order_date)}</div>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <div className="w-9 h-9 flex items-center justify-center bg-blue-100 rounded-lg flex-shrink-0">
                  <i className="ri-send-plane-line text-[#0066CC] text-sm"></i>
                </div>
                <div>
                  <div className="text-xs text-gray-400">Expédition</div>
                  <div className="text-sm font-semibold text-gray-900">{formatDate(shipment.ship_date)}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl">
                <div className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                  <i className="ri-calendar-check-line text-amber-600 text-sm"></i>
                </div>
                <div>
                  <div className="text-xs text-gray-400">Livraison estimée</div>
                  <div className="text-sm font-semibold text-gray-900">{formatDate(shipment.estimated_delivery)}</div>
                </div>
              </div>
              {shipment.actual_delivery_date && (
                <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl">
                  <div className="w-9 h-9 flex items-center justify-center bg-emerald-100 rounded-lg flex-shrink-0">
                    <i className="ri-checkbox-circle-line text-emerald-600 text-sm"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Livraison réelle</div>
                    <div className="text-sm font-semibold text-gray-900">{formatDate(shipment.actual_delivery_date)}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Adresses ── */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-100 flex items-center gap-2">
            <i className="ri-map-pin-user-line text-[#0066CC] text-lg"></i>
            <h3 className="font-semibold text-gray-900">Expéditeur &amp; Destinataire</h3>
          </div>
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Expéditeur */}
              {(shipment.sender_name || shipment.sender_country || shipment.origin) && (
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 flex items-center justify-center bg-[#0066CC] rounded-lg">
                      <i className="ri-send-plane-line text-white text-xs"></i>
                    </div>
                    <h4 className="font-semibold text-gray-900 text-sm">Expéditeur</h4>
                  </div>
                  <div className="space-y-1.5 text-sm">
                    {shipment.sender_name && <div className="font-semibold text-gray-900">{shipment.sender_name}</div>}
                    {shipment.sender_phone && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <i className="ri-phone-line text-gray-400 flex-shrink-0"></i>
                        <span className="break-all">{shipment.sender_phone}</span>
                      </div>
                    )}
                    {shipment.sender_address_line1 && (
                      <div className="flex items-start gap-2 text-gray-600">
                        <i className="ri-map-pin-line text-gray-400 flex-shrink-0 mt-0.5"></i>
                        <span>{shipment.sender_address_line1}</span>
                      </div>
                    )}
                    {(shipment.sender_city || shipment.sender_country) && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <i className="ri-building-line text-gray-400 flex-shrink-0"></i>
                        <span>{[shipment.sender_city, shipment.sender_country || shipment.origin].filter(Boolean).join(', ')}</span>
                      </div>
                    )}
                    {!shipment.sender_name && !shipment.sender_address_line1 && (
                      <div className="text-gray-600">{shipment.sender_country || shipment.origin}</div>
                    )}
                  </div>
                </div>
              )}

              {/* Destinataire */}
              <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 flex items-center justify-center bg-[#00CC88] rounded-lg">
                    <i className="ri-user-location-line text-white text-xs"></i>
                  </div>
                  <h4 className="font-semibold text-gray-900 text-sm">Destinataire</h4>
                </div>
                <div className="space-y-1.5 text-sm">
                  {shipment.recipient_name && <div className="font-semibold text-gray-900">{shipment.recipient_name}</div>}
                  {shipment.recipient_phone && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <i className="ri-phone-line text-gray-400 flex-shrink-0"></i>
                      <span className="break-all">{shipment.recipient_phone}</span>
                    </div>
                  )}
                  {shipment.recipient_email && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <i className="ri-mail-line text-gray-400 flex-shrink-0"></i>
                      <span className="break-all">{shipment.recipient_email}</span>
                    </div>
                  )}
                  {shipment.recipient_address_line1 && (
                    <div className="flex items-start gap-2 text-gray-600">
                      <i className="ri-map-pin-line text-gray-400 flex-shrink-0 mt-0.5"></i>
                      <span>{shipment.recipient_address_line1}</span>
                    </div>
                  )}
                  {shipment.recipient_address_line2 && (
                    <div className="flex items-start gap-2 text-gray-600">
                      <i className="ri-building-2-line text-gray-400 flex-shrink-0 mt-0.5"></i>
                      <span>{shipment.recipient_address_line2}</span>
                    </div>
                  )}
                  {(shipment.recipient_city || shipment.recipient_postal_code || shipment.recipient_country) && (
                    <div className="flex items-start gap-2 text-gray-600">
                      <i className="ri-building-line text-gray-400 flex-shrink-0 mt-0.5"></i>
                      <span>{[shipment.recipient_city, shipment.recipient_postal_code, shipment.recipient_country || shipment.destination].filter(Boolean).join(', ')}</span>
                    </div>
                  )}
                  {!shipment.recipient_name && !shipment.recipient_address_line1 && (
                    <div className="text-gray-600">{shipment.recipient_country || shipment.destination}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Détails produit ── */}
        {(shipment.product_name || shipment.product_photo_url) && (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-100 flex items-center gap-2">
              <i className="ri-shopping-bag-line text-[#0066CC] text-lg"></i>
              <h3 className="font-semibold text-gray-900">Détails du produit</h3>
            </div>
            <div className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row gap-4">
                {shipment.product_photo_url && (
                  <div className="w-full sm:w-32 h-40 sm:h-32 flex-shrink-0 rounded-xl overflow-hidden border border-gray-100">
                    <img src={shipment.product_photo_url} alt={shipment.product_name || 'Produit'} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex-1 grid grid-cols-2 gap-3">
                  {shipment.product_name && (
                    <div className="col-span-2">
                      <div className="text-xs text-gray-400 mb-0.5">Nom du produit</div>
                      <div className="font-semibold text-gray-900">{shipment.product_name}</div>
                    </div>
                  )}
                  {shipment.product_quantity && (
                    <div>
                      <div className="text-xs text-gray-400 mb-0.5">Quantité</div>
                      <div className="font-semibold text-gray-900">{shipment.product_quantity}</div>
                    </div>
                  )}
                  {shipment.product_price && (
                    <div>
                      <div className="text-xs text-gray-400 mb-0.5">Prix unitaire</div>
                      <div className="font-semibold text-gray-900">{shipment.product_price.toLocaleString()} {shipment.currency || 'USD'}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Paiement ── */}
        {(shipment.payment_method || shipment.total_amount) && (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-100 flex items-center gap-2">
              <i className="ri-bank-card-line text-[#0066CC] text-lg"></i>
              <h3 className="font-semibold text-gray-900">Informations de paiement</h3>
            </div>
            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {shipment.payment_method && (
                  <div>
                    <div className="text-xs text-gray-400 mb-1">Mode de paiement</div>
                    <div className="font-semibold text-gray-900">{shipment.payment_method}</div>
                  </div>
                )}
                {shipment.total_amount && (
                  <div>
                    <div className="text-xs text-gray-400 mb-1">Montant total</div>
                    <div className="font-semibold text-gray-900">{shipment.total_amount.toLocaleString()} {shipment.currency || 'USD'}</div>
                  </div>
                )}
                {shipment.invoice_url && (
                  <div>
                    <div className="text-xs text-gray-400 mb-1">Facture</div>
                    <a href={shipment.invoice_url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-[#0066CC] hover:underline font-semibold text-sm cursor-pointer">
                      <i className="ri-file-download-line"></i>
                      Télécharger
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Contenu & Notes ── */}
        {(shipment.package_contents || shipment.notes || shipment.photo_url) && (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-100 flex items-center gap-2">
              <i className="ri-file-list-line text-[#0066CC] text-lg"></i>
              <h3 className="font-semibold text-gray-900">Contenu &amp; Notes</h3>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              {shipment.package_contents && (
                <div>
                  <div className="text-xs text-gray-400 mb-1 uppercase tracking-wide">Contenu du colis</div>
                  <p className="text-gray-900 text-sm">{shipment.package_contents}</p>
                </div>
              )}
              {shipment.notes && (
                <div>
                  <div className="text-xs text-gray-400 mb-1 uppercase tracking-wide">Notes</div>
                  <p className="text-gray-900 text-sm">{shipment.notes}</p>
                </div>
              )}
              {shipment.photo_url && (
                <div>
                  <div className="text-xs text-gray-400 mb-2 uppercase tracking-wide">Photo du colis</div>
                  <div className="rounded-xl overflow-hidden border border-gray-100 max-w-sm">
                    <img src={shipment.photo_url} alt="Colis" className="w-full h-auto object-cover" />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── QR Code standalone (si pas affiché en haut) ── */}
        {qrDataUrl && !showQR && (
          <div className="bg-white rounded-2xl border border-gray-100 p-5 flex flex-col sm:flex-row items-center gap-5">
            <img src={qrDataUrl} alt="QR Code de suivi" className="w-28 h-28 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">QR Code de suivi</h4>
              <p className="text-sm text-gray-500">Scannez ce code pour accéder directement au suivi de ce colis depuis n'importe quel appareil.</p>
              <button
                onClick={() => {
                  const a = document.createElement('a');
                  a.href = qrDataUrl;
                  a.download = `qr-${shipment.tracking_number}.png`;
                  a.click();
                }}
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors cursor-pointer"
              >
                <i className="ri-download-2-line"></i>
                Télécharger le QR
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {showReceipt && (
        <ShipmentReceipt shipment={shipment} onClose={() => setShowReceipt(false)} />
      )}
    </>
  );
};

export default TrackingResults;
